import { useEffect, useState } from "react";
import { Check, Loader2, Plus, Search, X } from "lucide-react";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Input } from "ui/input";
import type { WalkInVisaApplicantFilters } from "services/visaService";
import type { Applicant } from "../VisaApplicantCard";

interface SelectApplicantsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  applicants: Applicant[];
  availableApplicants: Applicant[];

  onSelect: (applicants: Applicant[]) => void;

  filters?: WalkInVisaApplicantFilters;
  onFiltersChange?: (filters: WalkInVisaApplicantFilters) => void;
  isReadOnly?: boolean;
  isLoading?: boolean;
}

function GenderAvatar({
  gender,
  name,
  size = "md",
}: {
  gender: string;
  name: string;
  size?: "sm" | "md" | "lg";
}) {
  const isFemale = gender?.toLowerCase() === "female";

  const sizeClass =
    size === "sm"
      ? "h-7 w-7 text-sm"
      : size === "lg"
        ? "h-10 w-10 text-xl"
        : "h-8 w-8 text-base";

  return (
    <span
      title={`${name} · ${gender}`}
      aria-label={`${name}, ${gender}`}
      className={`flex shrink-0 items-center justify-center rounded-full border-2 border-white shadow-sm ${sizeClass} ${
        isFemale
          ? "bg-pink-100 text-pink-700"
          : "bg-blue-100 text-blue-700"
      }`}
    >
      {isFemale ? "👩" : "👨"}
    </span>
  );
}

export default function SelectApplicants({
  open,
  onOpenChange,
  applicants,
  availableApplicants,
  onSelect,
  isReadOnly = false,
  isLoading = false,
  filters,
  onFiltersChange,
}: SelectApplicantsProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingApplicants, setPendingApplicants] = useState<Applicant[]>([]);
  const pendingApplicantIds = pendingApplicants.map((applicant) => applicant.id);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setPendingApplicants([]);
    }
  }, [open]);

  const alreadyAddedIds = new Set(
    applicants.map((applicant) => applicant.id),
  );

  const filteredApplicants = availableApplicants.filter((applicant) => {
    if (alreadyAddedIds.has(applicant.id)) {
      return false;
    }

    if (onFiltersChange) return true;

    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return true;
    }

    const name =
      applicant.fullName ||
      [
        applicant.title,
        applicant.firstName,
        applicant.middleName,
        applicant.lastName,
      ]
        .filter(Boolean)
        .join(" ");

    return (
      name.toLowerCase().includes(query) ||
      applicant.itsNo?.toLowerCase().includes(query) ||
      applicant.passportNo?.toLowerCase().includes(query)
    );
  });

  const handleToggleApplicant = (id: string) => {
    if (isReadOnly) return;

    const applicant = availableApplicants.find((item) => item.id === id);
    if (!applicant) return;
    setPendingApplicants((current) =>
      current.some((item) => item.id === id)
        ? current.filter((item) => item.id !== id)
        : [...current, applicant],
    );
  };

  const handleClose = () => {
    setSearchQuery("");
    setPendingApplicants([]);
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onSelect(pendingApplicants.filter((applicant) => !alreadyAddedIds.has(applicant.id)));

    setSearchQuery("");
    setPendingApplicants([]);
    onOpenChange(false);
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-sky-100 bg-sky-50/70 p-5">
          <div>
            <h3 className="text-lg font-semibold text-sky-950">
              Select Applicants
            </h3>

            <p className="mt-0.5 text-sm text-sky-600">
              Select applicants with submitted visa applications
            </p>
          </div>

          <button
            type="button"
            className="rounded-lg p-2 text-slate-500 hover:bg-white"
            onClick={handleClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="border-b p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <Input
              disabled={isReadOnly}
              aria-label="Search applicants"
              value={filters?.searchText ?? searchQuery}
              onChange={(event) =>
                onFiltersChange
                  ? onFiltersChange({ searchText: event.target.value })
                  : setSearchQuery(event.target.value)
              }
              placeholder="Search by name, ITS number, reference ID or passport..."
              className="h-10 border-sky-200 pl-10"
            />
          </div>

        </div>

        {/* Applicant selection */}
        <div className="flex-1 overflow-y-auto p-4" aria-busy={isLoading}>
          {isLoading ? (
            <div
              role="status"
              className="flex min-h-48 flex-col items-center justify-center gap-3 py-12 text-sm text-sky-700"
            >
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
              <span>Loading applicants...</span>
            </div>
) : filteredApplicants.length === 0 ? (
  <div className="py-12 text-center text-sm text-slate-400">
    No applicants available
  </div>
) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {filteredApplicants.map((applicant) => {
                const selected = pendingApplicantIds.includes(
                  applicant.id,
                );

                const name =
                  applicant.fullName ||
                  [
                    applicant.title,
                    applicant.firstName,
                    applicant.middleName,
                    applicant.lastName,
                  ]
                    .filter(Boolean)
                    .join(" ");

                return (
                  <button
                    key={applicant.id}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() =>
                      handleToggleApplicant(applicant.id)
                    }
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${
                      selected
                        ? "border-sky-400 bg-sky-50"
                        : "border-slate-200 bg-white hover:border-sky-200 hover:bg-sky-50/50"
                    }`}
                  >
                    <GenderAvatar
                      gender={applicant.gender}
                      name={name}
                      size="lg"
                    />

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-900">
                        {name}
                      </div>

                      <p className="flex items-center gap-1 whitespace-nowrap text-[10px] leading-4 text-slate-500">
                        <span className="shrink-0">
                          ITS {applicant.itsNo || "-"} ·{" "}
                          {applicant.age}y · {applicant.gender}
                        </span>
                        {applicant.referenceId && (
                          <Badge
                            className="border-yellow-200 bg-yellow-100 px-1 py-0 text-[9px] leading-4 text-yellow-800"
                            title="Reference ID"
                          >
                            Ref: {applicant.referenceId}
                          </Badge>
                        )}
                      </p>

                      <p className="text-xs text-slate-500">
                        Passport {applicant.passportNo || "-"}
                      </p>
                    </div>

                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                        selected
                          ? "border-sky-600 bg-sky-600 text-white"
                          : "border-slate-300"
                      }`}
                    >
                      {selected && (
                        <Check className="h-3.5 w-3.5" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t bg-slate-50 p-4">
          <span className="text-xs text-slate-600">
            {pendingApplicantIds.length} selected
          </span>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              size="sm"
              disabled={
                isReadOnly ||
                pendingApplicantIds.length === 0
              }
              className="bg-sky-600 text-white hover:bg-sky-700"
              onClick={handleConfirm}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add Selected
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
